// Typed fetch client for the Fajr Arabi API.
// CLAUDE.md §27: every endpoint goes through this so we get one place to add
// retries, telemetry, etc. SDK package will absorb this later.

const STAGING_API = 'https://staging-api.fajrarabi.com';
const PROD_API = 'https://api.fajrarabi.com';

function defaultApiBase(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'admin.fajrarabi.com') return PROD_API;
    if (host === 'staging-admin.fajrarabi.com') return STAGING_API;
  }
  // Local dev — let Vite proxy or hit staging directly.
  return STAGING_API;
}

export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? defaultApiBase();

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

// M1 (motiflink §7.5): silent-refresh-on-401 — one deduped POST /auth/refresh,
// then retry the original request once. Mirrors apps/app/src/lib/api.ts.
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  refreshInFlight ??= fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

async function rawRequest(path: string, opts: ApiRequestOptions): Promise<Response> {
  const { method = 'GET', body, signal } = opts;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';

  return fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
}

const NO_REFRESH_PATHS = new Set(['/auth/login', '/auth/refresh', '/auth/logout']);

/**
 * Multipart upload with the SAME 401→refresh→retry and envelope unwrapping as
 * `api()`. M5, motiflink §5.4.
 *
 * ⚠️ Two things this exists to prevent, both of which bit the original build:
 *
 *  1. **Never set a Content-Type header on FormData.** The browser must set it
 *     itself so it can append the multipart boundary; forcing
 *     `multipart/form-data` produces a body the server cannot parse.
 *  2. **Never use a raw `fetch` for uploads.** The original hit an unset env
 *     base URL and silently posted to the wrong origin. Everything goes through
 *     API_BASE here.
 */
export async function apiUpload<T>(
  path: string,
  formData: FormData,
  opts: { signal?: AbortSignal } = {},
): Promise<T> {
  const send = () =>
    fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      // NO headers object — see (1) above.
      body: formData,
      signal: opts.signal,
    });

  let resp = await send();
  if (resp.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) resp = await send();
  }

  const text = await resp.text();
  const parsed: unknown = text ? (JSON.parse(text) as unknown) : null;

  if (!resp.ok) {
    const e = (parsed as { error?: { code: string; message: string; details?: unknown } })?.error;
    throw new ApiError(
      resp.status,
      e?.code ?? 'unknown',
      e?.message ?? `Upload failed (${resp.status})`,
      e?.details,
    );
  }

  return parsed as T;
}

export async function api<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
  let resp = await rawRequest(path, opts);

  if (resp.status === 401 && !NO_REFRESH_PATHS.has(path)) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      resp = await rawRequest(path, opts);
    }
  }

  if (resp.status === 204) {
    return undefined as T;
  }

  const text = await resp.text();
  const parsed: unknown = text ? (JSON.parse(text) as unknown) : null;

  if (!resp.ok) {
    const e = (parsed as { error?: { code: string; message: string; details?: unknown } })?.error;
    throw new ApiError(
      resp.status,
      e?.code ?? 'unknown',
      e?.message ?? `Request failed (${resp.status})`,
      e?.details,
    );
  }

  return parsed as T;
}
